"use client";

import React, { useEffect, useRef } from "react";
import { cellWet, generateMapTerrain, MAP_FACE, MAP_PICK_LIST, type EmpireMapDef, type GeneratedTerrain } from "./empire-maps";
import { avenueTrees, landRoutes } from "./empire-roads";
import { MAP_HALF, terrainHeight, terrainStyle, terrainTrees, TOWN_PADS, WATERLINE } from "./empire-terrain";

/** One pixel per world unit, so the card is the same land the match loads. */
const RES = 192;
const SPAN = MAP_HALF * 2;

const TOWN_COLORS = ["#2a62d6", "#2e9a45", "#d32626", "#7a35d4"];

const terrainCache = new Map<string, GeneratedTerrain>();

function terrainFor(map: EmpireMapDef): GeneratedTerrain {
  const hit = terrainCache.get(map.id);
  if (hit) return hit;
  const pads = TOWN_PADS.slice(0, 4).map((p) => ({ x: p.x, z: p.z }));
  const made = generateMapTerrain(map, pads);
  terrainCache.set(map.id, made);
  return made;
}

function speckle(x: number, z: number): number {
  const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

function toPx(x: number, z: number): [number, number] {
  return [((x + MAP_HALF) / SPAN) * RES, ((MAP_HALF - z) / SPAN) * RES];
}

/** Same height colors the match paints on the ground. */
function landColor(
  h: number,
  cliff: boolean,
  style: string,
  n: number,
  n2: number
): [number, number, number] {
  let r = style === "dry_arabia" ? 0.72 + n * 0.08 : 0.42 + n * 0.1;
  let g = style === "dry_arabia" ? 0.62 + n * 0.06 : 0.58 + n2 * 0.1;
  let b = style === "dry_arabia" ? 0.38 : 0.28 + n * 0.06;
  if (style === "woodlands" && !cliff) {
    r = 0.36 + n * 0.08;
    g = 0.52 + n * 0.08;
    b = 0.26;
  }
  if (cliff) {
    r = 0.42 + n * 0.08;
    g = 0.4 + n * 0.05;
    b = 0.36;
  } else if (h > 1.7) {
    const t = Math.min(1, (h - 1.7) / 0.7);
    r = r * (1 - t) + 0.9 * t;
    g = g * (1 - t) + 0.92 * t;
    b = b * (1 - t) + 0.94 * t;
  } else if (h > 0.22 && h < WATERLINE + 1.05) {
    const t = Math.max(0, Math.min(1, (h - 0.28) / 0.85));
    r = 0.74 * (1 - t) + r * t;
    g = 0.62 * (1 - t) + g * t;
    b = 0.38 * (1 - t) + b * t;
  }
  if (h < 0.22 && !cliff) {
    r = 0.1;
    g = 0.32;
    b = 0.48;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function drawMapPreview(canvas: HTMLCanvasElement, map: EmpireMapDef) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const terrain = terrainFor(map);
  const style = terrainStyle(map.biome);
  const img = ctx.createImageData(RES, RES);
  for (let iz = 0; iz < RES; iz++) {
    for (let ix = 0; ix < RES; ix++) {
      const x = (ix / (RES - 1)) * SPAN - MAP_HALF;
      const z = ((RES - 1 - iz) / (RES - 1)) * SPAN - MAP_HALF;
      const h = terrainHeight(x, z, map.seed, map.biome);
      const slope = Math.max(
        Math.abs(terrainHeight(x + 0.8, z, map.seed, map.biome) - h),
        Math.abs(terrainHeight(x, z + 0.8, map.seed, map.biome) - h)
      );
      const cliff = slope > 0.38 || (h < WATERLINE + 0.55 && h > 0.2 && slope > 0.22);
      const wet = cellWet(terrain.water, x, z);
      const beach = cellWet(terrain.beaches, x, z);
      let r: number;
      let g: number;
      let b: number;
      if (wet) {
        const shore =
          !cellWet(terrain.water, x + 2, z) ||
          !cellWet(terrain.water, x - 2, z) ||
          !cellWet(terrain.water, x, z + 2) ||
          !cellWet(terrain.water, x, z - 2);
        if (shore) {
          r = 142;
          g = 200;
          b = 220;
        } else {
          r = 20;
          g = 62;
          b = 120;
        }
      } else if (beach) {
        r = 206;
        g = 176;
        b = 118;
      } else {
        [r, g, b] = landColor(h, cliff, style, speckle(x * 0.37, z * 0.37), speckle(x * 1.4, z * 0.9));
      }
      const p = (iz * RES + ix) * 4;
      img.data[p] = r;
      img.data[p + 1] = g;
      img.data[p + 2] = b;
      img.data[p + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const routes = landRoutes(map.seed, map.biome);
  ctx.strokeStyle = "rgba(120, 86, 48, 0.9)";
  ctx.lineWidth = 1.4;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  for (const line of routes.lines) {
    if (line.length < 2) continue;
    ctx.beginPath();
    line.forEach(([x, z], i) => {
      const [px, py] = toPx(x, z);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();
  }

  const trees = [...terrainTrees(map.seed, map.biome), ...avenueTrees(map.seed, map.biome)];
  for (const t of trees) {
    if (cellWet(terrain.water, t.x, t.z)) continue;
    const [px, py] = toPx(t.x, t.z);
    const shade = 18 + Math.floor((t.hue || 0) * 28);
    ctx.fillStyle = `rgb(${shade}, ${70 + shade}, ${28})`;
    const s = Math.max(1.4, t.s * 1.6);
    ctx.fillRect(px - s * 0.5, py - s * 0.5, s, s);
  }

  for (const hub of terrain.resourceHubs) {
    if (hub.kind === "wood") continue;
    const [px, py] = toPx(hub.x, hub.z);
    ctx.fillStyle = hub.kind === "gold" ? "#e6c15a" : hub.kind === "stone" ? "#d5d0c6" : "#c45a3a";
    ctx.fillRect(px - 1.2, py - 1.2, 2.4, 2.4);
  }

  TOWN_PADS.slice(0, 4).forEach((pad, i) => {
    const [px, py] = toPx(pad.x, pad.z);
    ctx.fillStyle = "#f4efe4";
    ctx.fillRect(px - 4.2, py - 4.2, 8.4, 8.4);
    ctx.fillStyle = TOWN_COLORS[i];
    ctx.fillRect(px - 2.6, py - 2.6, 5.2, 5.2);
  });
}

function MapCard({
  map,
  selected,
  onPick,
}: {
  map: EmpireMapDef;
  selected: boolean;
  onPick: (id: string) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (c) drawMapPreview(c, map);
  }, [map]);
  return (
    <button
      type="button"
      className={selected ? "empire-map-card on" : "empire-map-card"}
      onClick={() => onPick(map.id)}
    >
      <canvas ref={ref} width={RES} height={RES} />
      <strong>{map.name}</strong>
      <span>{MAP_FACE[map.biome]}</span>
    </button>
  );
}

export function MapPicker({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <p className="empire-map-lead">
        {MAP_PICK_LIST.length} maps. The picture is the land you play: the same water, hills, forest, and corner towns.
      </p>
      <div className="empire-map-grid">
        {MAP_PICK_LIST.map((map) => (
          <MapCard key={map.id} map={map} selected={map.id === selected} onPick={onSelect} />
        ))}
      </div>
    </div>
  );
}

export { MAP_PICK_LIST };
